import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  User,
  Briefcase,
  GraduationCap,
  Star,
  FileText,
  FolderPlus,
  Eye,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export interface SectionVisibility {
  summary: boolean;
  skills: boolean;
  experience: boolean;
  education: boolean;
  customSections: boolean;
}

interface SectionVisibilityControlsProps {
  visibility: SectionVisibility;
  onChange: (visibility: SectionVisibility) => void;
}

const SECTIONS = [
  { key: "summary" as const, label: "Summary", icon: FileText },
  { key: "skills" as const, label: "Skills", icon: Star },
  { key: "experience" as const, label: "Experience", icon: Briefcase },
  { key: "education" as const, label: "Education", icon: GraduationCap },
  { key: "customSections" as const, label: "Custom Sections", icon: FolderPlus },
];

export const SectionVisibilityControls = ({
  visibility,
  onChange,
}: SectionVisibilityControlsProps) => {
  const toggleSection = (key: keyof SectionVisibility) => {
    onChange({ ...visibility, [key]: !visibility[key] });
  };

  const visibleCount = Object.values(visibility).filter(Boolean).length;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Eye className="w-4 h-4" />
          Sections ({visibleCount}/5)
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            Show/Hide Sections
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-sm text-muted-foreground mb-4">
            Toggle sections on or off. Hidden sections won't appear on your resume.
          </p>
          <div className="space-y-3">
            {SECTIONS.map(({ key, label, icon: Icon }) => (
              <div
                key={key}
                className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-4 h-4 text-muted-foreground" />
                  <Label htmlFor={`visibility-${key}`} className="cursor-pointer">
                    {label}
                  </Label>
                </div>
                <Switch
                  id={`visibility-${key}`}
                  checked={visibility[key]}
                  onCheckedChange={() => toggleSection(key)}
                />
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const defaultSectionVisibility: SectionVisibility = {
  summary: true,
  skills: true,
  experience: true,
  education: true,
  customSections: true,
};
